/** @module IglooUtils **/

/**
 * Exports the IglooUtils public functions
 */
module.exports = {
  guid: function() {
    // Get the current timestamp
    var d = new Date().getTime();

    // Add the high performance timestamp if possible
    if (window.performance && typeof window.performance.now === "function") {
      d += performance.now(); //use high-precision timer if available
    }

    // Create a GUID
    var guid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      var r = (d + Math.random() * 16) % 16 | 0;
      d = Math.floor(d / 16);
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
    return guid;
  },

  /**
   * This function checks for all of the keys in the given array
   * @param  {Object}  array_to_check The object to check for the given keys
   * @param  {Array}   keys           The array of keys to check the array for
   * @return {Boolean}                Whether the array contains all of the keys
   */
  arrayHasKeys: function(array_to_check, keys) {
    // Loop through all of the keys
    for (var i = 0; i < keys.length; i++) {
      if (typeof array_to_check[keys[i]] === 'undefined' || array_to_check[keys[i]] === '' || array_to_check[keys[i]] === null) {
        return false;
      }
    }

    return true;
  }, // --> arrayHasKeys

  /**
   * This function checks if a given element is 60% or more in the viewport
   * @param  {DOMElement}  element     The element to check for visibility
   * @param  {DOMElement}  debug_div   The debugging div to put the % visible in
   * @param  {Boolean}     igloo_debug The debugging state of igloo
   * @param  {Number}      min_percent The minimum percent for an element to be
   *                                   considered in view (default 0.6)
   * @return {Boolean}                 Whether the element is in view
   */
  elementIsVisible: function(element, debug_div, igloo_debug, min_percent) {
    // Check for things that cause bad things to happen
    if (typeof window.pageYOffset === 'undefined') {
      return false;
    }

    // Check for unfriendly iframe
    try {
      if (typeof window.top.document.body === 'undefined') {
        return false;
      }
    } catch (e) {
      return false;
    }

    if ([true, 'v', 'vv'].indexOf(igloo_debug) === -1) {
      debug_div = null;
    }

    // Default the min_percent to 0.6
    if (typeof min_percent !== 'number') {
      min_percent = 0.6;
    }

    try {
      // Get the visible pixels
      var visible_pixels = this.getElementVisiblePixels(element);

      // Get the possible pixels and calculate the percentage
      var element_offset = element.getBoundingClientRect();
      var total_pixels = element_offset.width * element_offset.height;
      var in_view_percent = total_pixels > 0 ? visible_pixels / total_pixels : 0;

      // Write to the debug div (if needed)
      this.setElementAttributes(debug_div, {
        html: Math.round(in_view_percent * 100) + '%',
      });

      return in_view_percent >= min_percent;
    } catch (e) {
      // Return false on errors
      this.setElementAttributes(debug_div, {
        html: 'ERR',
      });
      return false;
    }
  }, // --> elementIsVisible

  /**
   * Returns the number of visible pixels in the element
   * @param  {DOMElement} element The element to check
   * @return {Integer}            The number of visible pixels
   */
  getElementVisiblePixels: function(element) {
    // Initialize the offsets array
    var offsets = element.getBoundingClientRect();
    var offset_total = {
      bottom: offsets.bottom,
      left: offsets.left,
      right: offsets.right,
      top: offsets.top,
    };

    // Get the starting window
    var current_window = element.ownerDocument.defaultView || element.ownerDocument.parentWindow;

    // Loop until the top window
    var loop_count = 0;
    while (current_window !== window.top && current_window.frameElement !== null && loop_count++ <= 70) {
      try {
        // Check for undefined frameElement (Safari)
        if (typeof current_window.frameElement === 'undefined') {
          throw 'Safari Error';
        }

        // Get the next layer of offsets
        offsets = current_window.frameElement.getBoundingClientRect();

        // Add to the offset_total
        offset_total.left += offsets.left;
        offset_total.top += offsets.top;
        offset_total.right += (offsets.right - offsets.width);
        offset_total.bottom += (offsets.bottom - offsets.height);

        // Move up a level
        current_window = current_window.parent;
      } catch (e) {
        break;
      }
    }

    // Get the visible pixels
    var visible_pixels = this.comparePixels({
      v1: 0,
      v2: current_window.innerHeight,
    }, {
      v1: offset_total.top,
      v2: offset_total.bottom,
    }) * this.comparePixels({
      v1: 0,
      v2: current_window.innerWidth,
    }, {
      v1: offset_total.left,
      v2: offset_total.right,
    });

    return current_window !== window.top ? 0 : visible_pixels;
  }, // --> getElementVisiblePixels

  /**
   * This function returns the number of pixels in the viewport
   * @param  {Object} win_dim The window's dimensions
   * @param  {Object} div_dim The element's dimensions
   * @return {Number}         The number of pixels visible for the axis
   */
  comparePixels: function(win_dim, div_dim) {
    // If the item extends past both ends of the window
    if (div_dim.v1 <= win_dim.v1 && div_dim.v2 >= win_dim.v2) {
      return div_dim.v2 - div_dim.v1;
    }

    // If the item is entirely in the window
    if (div_dim.v1 >= win_dim.v1 && div_dim.v2 <= win_dim.v2) {
      return div_dim.v2 - div_dim.v1;
    }

    // If the item extends off the top of the window
    if (div_dim.v1 < win_dim.v1 && div_dim.v2 > win_dim.v1 && div_dim.v2 <= win_dim.v2) {
      return div_dim.v2 - win_dim.v1;
    }

    // If the item extends off the bottom of the window
    if (div_dim.v1 >= win_dim.v1 && div_dim.v1 < win_dim.v2 && div_dim.v2 > win_dim.v2) {
      return win_dim.v2 - div_dim.v1;
    }

    // The item is not in the window
    return 0;
  }, // --> comparePixels

  /**
   * This function sets multiple attributes on an HTML element
   * @param {DOMElement} element The element to set the attributes on
   * @param {Object}     attrs   The attributes to set on the element
   */
  setElementAttributes: function(element, attrs) {
    try {
      // Loop through the attributes
      for (var key in attrs) {
        // Switch for the type of thing
        switch (key) {
          case 'style':
            // Loop through all of the style attributes
            this.setElementStyle(element, attrs[key]);
            break;
          case 'html':
            // Set the innerHTML
            element.innerHTML = attrs[key];
            break;
          case 'text':
            // Set the innerText
            element.innerText = attrs[key];
            break;
          default:
            // Everything else just call setAttribute
            element.setAttribute(key, attrs[key]);
            break;
        }
      }

      return true;
    } catch (e) {
      return false;
    }
  }, // --> setElementAttributes

  /**
   * Set the style of the element
   * @param {DOMElement} element The element to affect
   * @param {Object}     style   The style attributes to set
   */
  setElementStyle: function(element, style) {
    // Loop through all of the style attributes
    for (var s in style) {
      element.style[s] = style[s];
    }
  }, // --> setElementStyle

  /**
   * Object.assign polyfill taken from
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/assign#Polyfill
   * @param  {Object} target The object to apply the parameters to
   * @return {Object}        The finished object
   */
  objectAssign: function(target) {
    'use strict';
    if (target === null) { // TypeError if undefined or null
      throw new TypeError('Cannot convert undefined or null to object');
    }

    var to = Object(target);

    for (var index = 1; index < arguments.length; index++) {
      var nextSource = arguments[index];

      if (nextSource !== null && typeof nextSource !== 'undefined') { // Skip over if undefined or null
        for (var nextKey in nextSource) {
          // Avoid bugs when hasOwnProperty is shadowed
          if (Object.prototype.hasOwnProperty.call(nextSource, nextKey) && typeof nextSource[nextKey] !== 'undefined' && nextSource[nextKey] !== null) {
            to[nextKey] = nextSource[nextKey];
          }
        }
      }
    }
    return to;
  }, // --> objectAssign

  /**
   * JS Implementation of md5
   * Taken from http://www.myersdaily.org/joseph/javascript/md5.js
   * @param  {string} key ASCII only
   * @return {string}     32 character string
   */
  hashString: (function() {
    /* jshint ignore:start */
    function md5cycle(x, k) {
      var a = x[0],
        b = x[1],
        c = x[2],
        d = x[3];

      a = ff(a, b, c, d, k[0], 7, -680876936);
      d = ff(d, a, b, c, k[1], 12, -389564586);
      c = ff(c, d, a, b, k[2], 17, 606105819);
      b = ff(b, c, d, a, k[3], 22, -1044525330);
      a = ff(a, b, c, d, k[4], 7, -176418897);
      d = ff(d, a, b, c, k[5], 12, 1200080426);
      c = ff(c, d, a, b, k[6], 17, -1473231341);
      b = ff(b, c, d, a, k[7], 22, -45705983);
      a = ff(a, b, c, d, k[8], 7, 1770035416);
      d = ff(d, a, b, c, k[9], 12, -1958414417);
      c = ff(c, d, a, b, k[10], 17, -42063);
      b = ff(b, c, d, a, k[11], 22, -1990404162);
      a = ff(a, b, c, d, k[12], 7, 1804603682);
      d = ff(d, a, b, c, k[13], 12, -40341101);
      c = ff(c, d, a, b, k[14], 17, -1502002290);
      b = ff(b, c, d, a, k[15], 22, 1236535329);

      a = gg(a, b, c, d, k[1], 5, -165796510);
      d = gg(d, a, b, c, k[6], 9, -1069501632);
      c = gg(c, d, a, b, k[11], 14, 643717713);
      b = gg(b, c, d, a, k[0], 20, -373897302);
      a = gg(a, b, c, d, k[5], 5, -701558691);
      d = gg(d, a, b, c, k[10], 9, 38016083);
      c = gg(c, d, a, b, k[15], 14, -660478335);
      b = gg(b, c, d, a, k[4], 20, -405537848);
      a = gg(a, b, c, d, k[9], 5, 568446438);
      d = gg(d, a, b, c, k[14], 9, -1019803690);
      c = gg(c, d, a, b, k[3], 14, -187363961);
      b = gg(b, c, d, a, k[8], 20, 1163531501);
      a = gg(a, b, c, d, k[13], 5, -1444681467);
      d = gg(d, a, b, c, k[2], 9, -51403784);
      c = gg(c, d, a, b, k[7], 14, 1735328473);
      b = gg(b, c, d, a, k[12], 20, -1926607734);

      a = hh(a, b, c, d, k[5], 4, -378558);
      d = hh(d, a, b, c, k[8], 11, -2022574463);
      c = hh(c, d, a, b, k[11], 16, 1839030562);
      b = hh(b, c, d, a, k[14], 23, -35309556);
      a = hh(a, b, c, d, k[1], 4, -1530992060);
      d = hh(d, a, b, c, k[4], 11, 1272893353);
      c = hh(c, d, a, b, k[7], 16, -155497632);
      b = hh(b, c, d, a, k[10], 23, -1094730640);
      a = hh(a, b, c, d, k[13], 4, 681279174);
      d = hh(d, a, b, c, k[0], 11, -358537222);
      c = hh(c, d, a, b, k[3], 16, -722521979);
      b = hh(b, c, d, a, k[6], 23, 76029189);
      a = hh(a, b, c, d, k[9], 4, -640364487);
      d = hh(d, a, b, c, k[12], 11, -421815835);
      c = hh(c, d, a, b, k[15], 16, 530742520);
      b = hh(b, c, d, a, k[2], 23, -995338651);

      a = ii(a, b, c, d, k[0], 6, -198630844);
      d = ii(d, a, b, c, k[7], 10, 1126891415);
      c = ii(c, d, a, b, k[14], 15, -1416354905);
      b = ii(b, c, d, a, k[5], 21, -57434055);
      a = ii(a, b, c, d, k[12], 6, 1700485571);
      d = ii(d, a, b, c, k[3], 10, -1894986606);
      c = ii(c, d, a, b, k[10], 15, -1051523);
      b = ii(b, c, d, a, k[1], 21, -2054922799);
      a = ii(a, b, c, d, k[8], 6, 1873313359);
      d = ii(d, a, b, c, k[15], 10, -30611744);
      c = ii(c, d, a, b, k[6], 15, -1560198380);
      b = ii(b, c, d, a, k[13], 21, 1309151649);
      a = ii(a, b, c, d, k[4], 6, -145523070);
      d = ii(d, a, b, c, k[11], 10, -1120210379);
      c = ii(c, d, a, b, k[2], 15, 718787259);
      b = ii(b, c, d, a, k[9], 21, -343485551);

      x[0] = add32(a, x[0]);
      x[1] = add32(b, x[1]);
      x[2] = add32(c, x[2]);
      x[3] = add32(d, x[3]);

    }

    function cmn(q, a, b, x, s, t) {
      a = add32(add32(a, q), add32(x, t));
      return add32((a << s) | (a >>> (32 - s)), b);
    }

    function ff(a, b, c, d, x, s, t) {
      return cmn((b & c) | ((~b) & d), a, b, x, s, t);
    }

    function gg(a, b, c, d, x, s, t) {
      return cmn((b & d) | (c & (~d)), a, b, x, s, t);
    }

    function hh(a, b, c, d, x, s, t) {
      return cmn(b ^ c ^ d, a, b, x, s, t);
    }

    function ii(a, b, c, d, x, s, t) {
      return cmn(c ^ (b | (~d)), a, b, x, s, t);
    }

    function md51(s) {
      var n = s.length,
        state = [1732584193, -271733879, -1732584194, 271733878],
        i;
      for (i = 64; i <= s.length; i += 64) {
        md5cycle(state, md5blk(s.substring(i - 64, i)));
      }
      s = s.substring(i - 64);
      var tail = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      for (i = 0; i < s.length; i++)
        tail[i >> 2] |= s.charCodeAt(i) << ((i % 4) << 3);
      tail[i >> 2] |= 0x80 << ((i % 4) << 3);
      if (i > 55) {
        md5cycle(state, tail);
        for (i = 0; i < 16; i++) tail[i] = 0;
      }
      tail[14] = n * 8;
      md5cycle(state, tail);
      return state;
    }

    /* there needs to be support for Unicode here,
     * unless we pretend that we can redefine the MD-5
     * algorithm for multi-byte characters (perhaps
     * by adding every four 16-bit characters and
     * shortening the sum to 32 bits). Otherwise
     * I suggest performing MD-5 as if every character
     * was two bytes--e.g., 0040 0025 = @%--but then
     * how will an ordinary MD-5 sum be matched?
     * There is no way to standardize text to something
     * like UTF-8 before transformation; speed cost is
     * utterly prohibitive. The JavaScript standard
     * itself needs to look at this: it should start
     * providing access to strings as preformed UTF-8
     * 8-bit unsigned value arrays.
     */
    function md5blk(s) { /* I figured global was faster.   */
      var md5blks = [],
        i; /* Andy King said do it this way. */
      for (i = 0; i < 64; i += 4) {
        md5blks[i >> 2] = s.charCodeAt(i) +
          (s.charCodeAt(i + 1) << 8) +
          (s.charCodeAt(i + 2) << 16) +
          (s.charCodeAt(i + 3) << 24);
      }
      return md5blks;
    }

    var hex_chr = '0123456789abcdef'.split('');

    function rhex(n) {
      var s = '',
        j = 0;
      for (; j < 4; j++)
        s += hex_chr[(n >> (j * 8 + 4)) & 0x0F] +
        hex_chr[(n >> (j * 8)) & 0x0F];
      return s;
    }

    function hex(x) {
      for (var i = 0; i < x.length; i++)
        x[i] = rhex(x[i]);
      return x.join('');
    }

    function md5(s) {
      return hex(md51(s));
    }

    /* this function is much faster,
    so if possible we use it. Some IEs
    are the only ones I know of that
    need the idiotic second function,
    generated by an if clause.  */

    function add32(a, b) {
      return (a + b) & 0xFFFFFFFF;
    }

    if (md5('hello') !== '5d41402abc4b2a76b9719d911017c592') {
      function add32(x, y) {
        var lsw = (x & 0xFFFF) + (y & 0xFFFF),
          msw = (x >> 16) + (y >> 16) + (lsw >> 16);
        return (msw << 16) | (lsw & 0xFFFF);
      }
    }

    return md5;
  /* jshint ignore:end */
  })(), // --> hashString
};
